// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useMemo, useReducer } from 'react';

import { FLAT_BLACK_FILLER, type HexTriplet, type MachineElement } from '@evolvemep/api-ts';
import { useMantineTheme } from '@mantine/core';

import { isNotNilOrEmptyString } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';

/** Color settings for the Bend Model Browser */
type BendModelColorState = {
  elementColor: HexTriplet;
  spoolColor: HexTriplet;
};

/** Toggleable/boolean properties of the Bend Model Browser */
type BendModelToggleState = {
  fitToView: boolean;
  ghosting: boolean;
  spoolColorEnabled: boolean;
  elementColorEnabled: boolean;
};

type IsolationStateOption = 'spool' | 'element' | 'model';

type IsolationState = {
  /** Specifies which part of the machine element to isolate. If 'model' is specified, the entire model will be shown. */
  isolating: IsolationStateOption;
};

/**
 * An object containing all the above states.
 */
export type BendModelBrowserState = BendModelColorState & BendModelToggleState & IsolationState;

type InitialStateOptions = {
  isolating: IsolationStateOption;
  spoolColor: HexTriplet;
  elementColor: HexTriplet;
};

const getInitialState = ({ spoolColor, elementColor, isolating }: InitialStateOptions): BendModelBrowserState => ({
  fitToView: true,
  ghosting: false,
  spoolColorEnabled: false,
  elementColorEnabled: true,
  spoolColor,
  elementColor,
  isolating,
});

type BendModelBrowserAction =
  | {
      type: 'toggle';
      property: keyof BendModelToggleState;
    }
  | {
      type: 'setColor';
      property: keyof BendModelColorState;
      value: HexTriplet;
    }
  | { type: 'isolate'; value: IsolationStateOption };

const bendModelBrowserStateReducer = (state: BendModelBrowserState, action: BendModelBrowserAction) => {
  if (action.type === 'toggle') {
    return { ...state, [action.property]: !state[action.property] };
  } else if (action.type === 'isolate') {
    return { ...state, isolating: action.value };
  }
  return { ...state, [action.property]: action.value };
};

/**
 * Custom hook to manage the state of the Bend Model Browser, including color settings, toggles, and isolation state.
 * @param element - The machine element for which the Bend Model Browser state is being managed.
 */
export const useBendModelBrowserState = (element: MachineElement) => {
  const theme = useMantineTheme();
  const initArg: InitialStateOptions = useMemo(
    () => ({
      spoolColor: (theme.colors.evolve?.[6] as HexTriplet | undefined) ?? FLAT_BLACK_FILLER,
      elementColor: (theme.colors.evolve?.[3] as HexTriplet | undefined) ?? FLAT_BLACK_FILLER,
      isolating: isNotNilOrEmptyString(element.spool) ? 'spool' : 'element',
    }),
    [theme, element],
  );
  const [state, dispatch] = useReducer(bendModelBrowserStateReducer, initArg, getInitialState);
  const toggle = useCallback(
    (property: keyof BendModelToggleState) => dispatch({ type: 'toggle', property }),
    [dispatch],
  );
  const setColor = useCallback(
    (property: keyof BendModelColorState, value: HexTriplet) => dispatch({ type: 'setColor', property, value }),
    [dispatch],
  );
  const isolate = useCallback((value: IsolationStateOption) => dispatch({ type: 'isolate', value }), [dispatch]);

  return { state, toggle, setColor, isolate };
};
